// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { qtyFieldStyles } from "@conklin/App/Components/Qty/QtyField";
import { FormFieldLabel } from "@conklin/MobiusOverrides/FormField";
import { fontConfig } from "@conklin/XDL/PreStyle/typography";
import { color, xScreen } from "@conklin/XDL/Utilities";
import { StyledButton } from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

qtyFieldStyles.actionButton = {
    css: css`
        background-color: ${color("common.ui1")};
        display: flex;
        flex: 0 0 20px;
        align-items: center;
        height: 100%;
        justify-content: center;
        ${IconWrapper} {
            color: ${color("common.ui5")};
        }
        &:first-of-type {
            border-right: 1px solid ${color("common.ui5")};
        }

        &:last-of-type {
            border-left: 1px solid ${color("common.ui5")};
        }

        &:disabled ${IconWrapper} {
            opacity: 0.5;
        }
        &:hover:not(:disabled) {
            background: ${color("common.ui10")};

            &:first-of-type,
            &:last-of-type {
                border-color: ${color("common.ui10")};
            }

            ${IconWrapper} {
                color: ${color("common.ui1")};
            }
        }
    `,
};

qtyFieldStyles.wrapper = {
    css: css`
        background: ${color("common.ui1")};
        display: flex;
        align-items: center;
        height: 28px;
        min-width: 96px;
        width: 96px;
        overflow: hidden;
        overflow-x: hidden;
        ${({ showActionButton }: any) =>
            !showActionButton &&
            `
                min-width: 60px;
                width: 60px;
        `}
        &:not(${FormFieldLabel}) {
            border: 1px solid ${color("common.ui6")};

            &:hover {
                border-color: ${color("common.ui10")};
            }

            &:focus-within {
                border-color: ${color("common.ui10")};
                > ${StyledButton} {
                    border-color: ${color("common.ui10")};
                }
            }
        }
    `,
};

qtyFieldStyles.editableQuantityTextField = {
    sizeVariant: "small",
    mergeCss: true,
    cssOverrides: {
        formField: css`
            ${TypographyStyle} {
                text-align: center;
            }

            &&& {
                input {
                    &,
                    &:hover:not(:disabled),
                    &:focus:not(:disabled) {
                        border: none;
                        border-radius: 0;
                        outline: none;
                        box-shadow: none;
                        padding: 0 4px;
                    }
                }

                ${({ showActionButton }: any) =>
                    !showActionButton &&
                    `
                    input{
                        height: 28px;
                    }
                `}
            }
        `,
        formInputWrapper: css`
            &,
            &:hover:not(:disabled),
            &:focus:not(:disabled) {
                border: none;
                box-shadow: none;
                padding: 0;
            }
        `,
        inputSelect: css`
            font-size: 14px;
            line-height: 17px;
            text-align: center;
            height: 28px;
        `,
    },
};

qtyFieldStyles.container = {
    css: css`
        display: inline-flex;
        gap: 6px;
        align-items: center;
        flex-wrap: wrap;
        max-width: 96px;
    `,
};

qtyFieldStyles.questionIconStyles = {
    size: 14,
    css: css`
        &,
        & > svg {
            opacity: 0.44;
            color: ${color("secondary")};
            &:hover {
                opacity: 1;
            }
        }
    `,
};

qtyFieldStyles.valueText = {
    size: 16,
    lineHeight: "17px",
    weight: fontConfig.weight.bold,
};

qtyFieldStyles.qtyLabel = {
    size: fontConfig.fontSize.small,
    lineHeight: fontConfig.lineHeight.small,
    weight: fontConfig.weight.regular,
};

qtyFieldStyles.qtyLabelNotEditable = {
    size: 12,
    lineHeight: "13px",
    weight: "normal",
    css: css`
        line-height: 13px;
        text-transform: capitalize;
        padding: 0;
        margin-bottom: 8px;
        display: none;
        ${xScreen("lte", "md")} {
            display: block;
        }
        @media print {
            &&& {
                font-size: 14px;
                line-height: 16px;
            }
        }
    `,
};

qtyFieldStyles.qtyErrorMessage = {
    size: 14,
    lineHeight: "22px",
    css: css`
        flex: 0 0 110px;
    `,
};

qtyFieldStyles.noPriceTooltip = {
    extendedStyles: {
        triggerWrapper: {
            css: css`
                margin-left: 4px;
                margin-bottom: 5px;
            `,
        },
    },
};

qtyFieldStyles.noPriceIcon = {
    size: 20,
    color: "warning.main",
    css: css`
        &,
        & svg {
            height: auto;
        }
    `,
};

qtyFieldStyles.qtyLabelNotEditable = {
    size: 12,
    lineHeight: "14px",
    css: css`
        display: none;
        ${xScreen("lte", "md")} {
            display: block;
            margin-bottom: 4px;
        }
    `,
};
